import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { setView, setRightContent, setActiveConversation } from '../redux/chat.redux';
import type { RightContent } from '../redux/chat.redux';
import ChatList from './chat-list';
import DetailArea from './detail-area';
import ProfilePanel from './panels/profile-panel';
import MobileBottomNav, { MobileTab } from './mobile-bottom-nav';

const PANEL_TITLES: Record<string, string> = {
  profile: '我的资料',
  'add-friend': '添加好友',
  requests: '新的好友',
  blocked: '黑名单',
  'new-group': '创建群聊',
};

function detailTitle(rc: RightContent, convName: string): string {
  switch (rc.type) {
    case 'chat':
      return convName || '聊天';
    case 'contact-card':
      return '个人名片';
    case 'group-detail':
      return '群聊详情';
    case 'panel':
      return PANEL_TITLES[rc.panel] || '';
    default:
      return '';
  }
}

// 移动端布局:列表(含底部气泡导航)↔ 全屏详情(顶栏返回)
// 「我的」为一级页面(内联展示,无返回);返回时取消列表选中态
export default function MobileLayout() {
  const dispatch = useAppDispatch();
  const view = useAppSelector((s) => s.chat.view);
  const rightContent = useAppSelector((s) => s.chat.rightContent);
  const activeId = useAppSelector((s) => s.chat.activeConversationId);
  const conversations = useAppSelector((s) => s.chat.conversations);
  const unreadTotal = useAppSelector((s) => s.chat.conversations.reduce((sum, c) => sum + c.unread, 0));
  const pendingCount = useAppSelector((s) => s.chat.pendingRequests.length);

  const [detailOpen, setDetailOpen] = useState(false);
  const isDetail = rightContent.type !== 'none';
  const isProfilePage = rightContent.type === 'panel' && rightContent.panel === 'profile';
  const activeConv = conversations.find((c) => c.id === activeId);

  const activeTab: MobileTab = isProfilePage ? 'profile' : view;

  const handleSelect = (tab: MobileTab) => {
    if (tab === 'profile') {
      dispatch(setRightContent({ type: 'panel', panel: 'profile' }));
    } else {
      dispatch(setView(tab));
    }
    // 切 Tab 关闭详情并取消列表选中态
    setDetailOpen(false);
    dispatch(setActiveConversation(null));
  };

  // 返回:关闭详情并取消列表选中态
  const handleBack = () => {
    setDetailOpen(false);
    dispatch(setActiveConversation(null));
  };

  return (
    <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
      {detailOpen && isDetail ? (
        <>
          {/* 详情顶栏(仅返回) */}
          <Box
            sx={{
              height: 56,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'primary.main',
              color: '#fff',
              flexShrink: 0,
              gap: 0.5,
            }}
          >
            <IconButton onClick={handleBack} sx={{ color: '#fff' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography noWrap sx={{ flex: 1, fontWeight: 600 }}>
              {detailTitle(rightContent, activeConv?.display_name || '')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <DetailArea embedded />
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {isProfilePage ? (
              // 「我的」一级页面:内联展示,无返回
              <ProfilePanel embedded onClose={() => dispatch(setRightContent({ type: 'none' }))} />
            ) : (
              <ChatList onOpenDetail={() => setDetailOpen(true)} />
            )}
          </Box>
          <MobileBottomNav activeTab={activeTab} unreadTotal={unreadTotal} pendingCount={pendingCount} onSelect={handleSelect} />
        </>
      )}
    </Box>
  );
}
